import { Box, Button, Stack, Typography } from "@mui/material";
import React from "react";
import { useTheme } from "@mui/material/styles";
import useResponsive from "../hooks/useResponsive";
import {
  formatDateFromString,
  formatTextType,
  limitToSeven,
} from "../utils/url-id";

const headerSty = {
  fontFamily: "Lexend",
  letterSpacing: "0.01em",
  fontWeight: 400,
};
const contentSty = {
  fontFamily: "Lexend",
  letterSpacing: "0.01em",
  fontWeight: 400,
};

interface ProfileUIProps {
  name: string;
  email: string;
  staffId: string;
  folio_number?: string;
}
export const ProfileUI = ({
  name,
  email,
  staffId,
  folio_number,
}: ProfileUIProps) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  return (
    <Box
      sx={{
        backgroundColor: "#F7F8F9",
        borderRadius: "4",
        paddingLeft: 3,
        paddingY: 2,
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        minHeight: 62,
      }}
    >
      <Stack sx={{ marginX: 3, marginY: isMedium ? 2 : 0 }}>
        <img
          src="/assets/icons/employee-icon.png"
          alt=""
          width={60}
          height={60}
        />
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{ paddingY: 1, marginX: 3, marginY: isMedium ? 2 : 0 }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Name
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {name}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{ paddingY: 1, marginX: 3, marginY: isMedium ? 2 : 0 }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Email
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {email}
        </Typography>
      </Stack>

      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{ paddingY: 1, marginX: 3, marginY: isMedium ? 2 : 0 }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Staff ID
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {limitToSeven(staffId) ?? "-"}
        </Typography>
      </Stack>
      {folio_number && (
        <Stack
          direction={"column"}
          justifyContent={"space-between"}
          sx={{ paddingY: 1, marginX: 3, marginY: isMedium ? 2 : 0 }}
        >
          <Typography
            variant="caption"
            sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
          >
            Folio Number
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ ...contentSty, color: theme.palette.neutral.main }}
          >
            {folio_number}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

interface ProfileUIHospitalProps {
  name: string;
  email: string;
  facilityId: string;
}
export const ProfileHospitalUI = ({
  name,
  email,
  facilityId,
}: ProfileUIHospitalProps) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  const isSmall = useResponsive("down", "sm", "xl");
  return (
    <Box
      sx={{
        backgroundColor: "#F7F8F9",
        borderRadius: "4",
        paddingLeft: 3,
        paddingY: 2,
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        minHeight: 62,
      }}
    >
      <Stack sx={{ marginX: 3, marginY: isMedium ? 2 : 0 }}>
        <img
          src="/assets/icons/employee-icon.png"
          alt=""
          width={60}
          height={60}
        />
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{ paddingY: 1, marginX: 3, marginY: isMedium ? 2 : 0 }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Name
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {name}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{ paddingY: 1, marginX: 3, marginY: isMedium ? 2 : 0 }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Email
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {email}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{ paddingY: 1, marginX: 3, marginY: isMedium ? 2 : 0 }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Facility ID
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {limitToSeven(facilityId) ?? "-"}
        </Typography>
      </Stack>{" "}
    </Box>
  );
};

interface DetailsUIProps {
  type: string;
  title: string;
  department: string;
  startDate: string;
  endDate: string;
}
export const DetailsUI = ({
  type,
  title,
  department,
  startDate,
  endDate,
}: DetailsUIProps) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");

  return (
    <Box
      sx={{
        backgroundColor: "#F7F8F9",
        borderRadius: "4",
        paddingLeft: 3,
        paddingY: "1.3rem",
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        minHeight: 70,
      }}
    >
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Type
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {type}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Title
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {title}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Department
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {department}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Start Date
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {startDate}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          End Date
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {endDate}
        </Typography>
      </Stack>{" "}
    </Box>
  );
};

export const DetailsHospitalUI = ({
  type,
  size,
  IsVerified,
  JoinedOn,
}: {
  type: string;
  size: string;
  IsVerified: boolean;
  JoinedOn: string;
}) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");

  return (
    <Box
      sx={{
        backgroundColor: "#F7F8F9",
        borderRadius: "4",
        paddingLeft: 3,
        paddingY: "1.3rem",
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        minHeight: 70,
      }}
    >
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Type
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {type}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Size
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {size}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          IsVerified
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {`${IsVerified}`}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Joined On
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {JoinedOn}
        </Typography>
      </Stack>
    </Box>
  );
};

export const VerificationHospitalUI = ({
  specialties,
  state,
  reg_no,
  verification,
  level,
}: {
  specialties: string;
  state: string;
  reg_no: string;
  verification: string;
  level: number;
}) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");

  return (
    <Box
      sx={{
        my: 3,
        backgroundColor: "#F7F8F9",
        borderRadius: "4",
        paddingLeft: 3,
        paddingY: "1.3rem",
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        minHeight: 70,
      }}
    >
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Specialities
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {specialties}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          State
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {state}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Verification
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {`${verification}`}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Reg No.
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {reg_no}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Level
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {level}
        </Typography>
      </Stack>
    </Box>
  );
};

interface PersonnelHighlightProps {
  doctors: string;
  nurses: string;
  support: string;
  student: string;
  available_jobs: string;
  total_jobs: string;
}

export const PersonnelHighlight = ({
  doctors,
  nurses,
  support,
  student,
  available_jobs,
  total_jobs,
}: PersonnelHighlightProps) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");

  return (
    <Box
      sx={{
        backgroundColor: "#F7F8F9",
        borderRadius: "4",
        paddingLeft: 3,
        paddingY: "1.3rem",
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        minHeight: 70,
      }}
    >
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Doctors
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {doctors}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Nurses
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {nurses}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Support staff
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {support}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Student Doctors
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {student}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Available Jobs
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {available_jobs}
        </Typography>
      </Stack>{" "}
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Total Jobs
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {total_jobs}
        </Typography>
      </Stack>
    </Box>
  );
};

interface ActiveEmploymentsProps {
  hospital: string;
  type: string;
  department: string;
  start_date: string;
  end_date: string;
  status: string;
  job_title: string;
}

export const ActiveEmployments = ({
  hospital,
  type,
  department,
  start_date,
  job_title,
  end_date,
  status,
}: ActiveEmploymentsProps) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");

  return (
    <Box
      sx={{
        backgroundColor: "#F7F8F9",
        borderRadius: "4",
        paddingLeft: 3,
        paddingY: "1.3rem",
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        minHeight: 70,
        my: 3,
      }}
    >
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Hospital
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {hospital}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Job Title
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {job_title}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Type
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {formatTextType(type ?? "")}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Department
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {department}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Start Date
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {formatDateFromString(start_date)}
        </Typography>
      </Stack>
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          End Date
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {formatDateFromString(end_date)}
        </Typography>
      </Stack>{" "}
      <Stack
        direction={"column"}
        justifyContent={"space-between"}
        sx={{
          paddingY: 1,
          marginX: isMedium ? 3 : 4,
          marginY: isMedium ? 2 : 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
        >
          Status
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ ...contentSty, color: theme.palette.neutral.main }}
        >
          {formatTextType(status)}
        </Typography>
      </Stack>
    </Box>
  );
};

interface EmployeeDataProps {
  startDate: string;
  endDate: string;
  requestType: string;
  amount: string;
  availableTime: string;
}

export const EmployeeRequest = ({
  startDate,
  endDate,
  requestType,
  amount,
  availableTime,
}: EmployeeDataProps) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  return (
    <Box
      sx={{
        border: `1px solid #DCE1EA`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMedium ? "column" : "row",
          py: isMedium ? 3 : 4,
          px: isMedium ? 3 : 4,
        }}
      >
        <Stack sx={{ width: isMedium ? "100%" : "25%", my: 2 }}>
          <Stack direction="row" sx={{ gap: 2, my: 1 }}>
            <img
              src="/assets/icons/clock-icon.svg"
              alt=""
              width={23}
              height={23}
            />
            <Typography variant="subtitle1" color={"primary"}>
              {" "}
              Pending Approval
            </Typography>
          </Stack>
          <Stack direction="row" sx={{ gap: 2, paddingLeft: 3 }}>
            <Typography
              sx={{ color: "#9199A8", fontSize: 9, letterSpacing: "1%" }}
            >
              Application ID:
            </Typography>
            <Typography
              sx={{ color: "#9199A8", fontSize: 9, letterSpacing: "1%" }}
            >
              50001890
            </Typography>
          </Stack>
        </Stack>
        <Stack sx={{ width: isMedium ? "100%" : "55%", my: 2 }}>
          <Typography
            color={"neutral"}
            sx={{
              fontFamily: "Lexend",
              fontSize: "10px",
              fontWeight: 300,
              lineHeight: "24.34px",
              letterSpacing: "0.01em",
            }}
          >
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Dolor
            malesuada velit et, vulputate congue augue vestibulum est. Ac
            pulvinar viverra et ultrices. Ornare.
          </Typography>
        </Stack>
        <Stack
          direction="row"
          sx={{ width: isMedium ? "100%" : "30%", gap: 3, my: 2 }}
          justifyContent={"flex-end"}
        >
          <Button variant="outlined" sx={{ borderRadius: "12px", height: 45 }}>
            Reject
          </Button>
          <Button sx={{ borderRadius: "12px", height: 45 }} variant="contained">
            Approve
          </Button>
        </Stack>
      </Box>
      <Box
        sx={{
          backgroundColor: "#F7F8F9",
          borderRadius: "4",
          paddingLeft: 3,
          paddingY: "1.3rem",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          minHeight: 70,
        }}
      >
        <Stack
          direction={"column"}
          justifyContent={"space-between"}
          sx={{
            paddingY: 1,
            marginX: isMedium ? 3 : 4,
            marginY: isMedium ? 2 : 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
          >
            Request type
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ ...contentSty, color: theme.palette.neutral.main }}
          >
            {requestType}
          </Typography>
        </Stack>
        <Stack
          direction={"column"}
          justifyContent={"space-between"}
          sx={{
            paddingY: 1,
            marginX: isMedium ? 3 : 4,
            marginY: isMedium ? 2 : 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
          >
            Amount
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ ...contentSty, color: theme.palette.neutral.main }}
          >
            {amount}
          </Typography>
        </Stack>

        <Stack
          direction={"column"}
          justifyContent={"space-between"}
          sx={{
            paddingY: 1,
            marginX: isMedium ? 3 : 4,
            marginY: isMedium ? 2 : 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
          >
            Start Date
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ ...contentSty, color: theme.palette.neutral.main }}
          >
            {startDate}
          </Typography>
        </Stack>
        <Stack
          direction={"column"}
          justifyContent={"space-between"}
          sx={{
            paddingY: 1,
            marginX: isMedium ? 3 : 4,
            marginY: isMedium ? 2 : 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
          >
            End Date
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ ...contentSty, color: theme.palette.neutral.main }}
          >
            {endDate}
          </Typography>
        </Stack>
        <Stack
          direction={"column"}
          justifyContent={"space-between"}
          sx={{
            paddingY: 1,
            marginX: isMedium ? 3 : 4,
            marginY: isMedium ? 2 : 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{ ...headerSty, color: theme.palette.neutral.contrastText }}
          >
            Available time
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ ...contentSty, color: theme.palette.neutral.main }}
          >
            {availableTime}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};
